import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	FormControl,
	Grid2,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { Rollset } from "@/pages/rollshop/models/Rollset";
import { upsertRoll } from "@/pages/rollshop/repositories/OperationRepository";
import { RollSelector } from "@/shared/selectors";
import { useRolls } from "@/shared/selectors/roll/RollSelector";
import { Controller, useForm } from "react-hook-form";
import { upsertRollset } from "../../repositories/RollsetRepository";
import { SectionSelector } from "../SectionSelector";
import {
	getDiameterError,
	getPassCompatibilityError,
} from "@/pages/rollshop/pages/rollsets/components/RollsetAssemblyModal";

type Props = {
	show: boolean;
	onHide: (rollsetId?: number) => void;
	sectionId: number | null;
	standId: number | null;
	standName: string | null;
	activeLetters: Set<string>;
};

const ROLLSET_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

export const NewRollsetDialog = ({
	show,
	onHide,
	sectionId,
	standId,
	standName,
	activeLetters,
}: Props) => {
	const queryClient = useQueryClient();

	const [error, setError] = useState("");
	const [confirmMessage, setConfirmMessage] = useState("");

	const [initialTopRoll, setInitialTopRoll] = useState<{
		diameter: number | null;
		standId: number | null;
	} | null>(null);
	const [initialBottomRoll, setInitialBottomRoll] = useState<{
		diameter: number | null;
		standId: number | null;
	} | null>(null);

	const { data: rolls } = useRolls({
		sectionId: sectionId ?? undefined,
		onError: (error) => setError(error.message),
	});

	const { control, reset, watch, trigger, setValue } = useForm<Rollset>({
		mode: "onChange",
	});

	const topRollId = watch("TopRollId");
	const topRollDiameter = watch("TopRollDiameter") ?? null;
	const topRollType = watch("TopRollType") ?? null;
	const bottomRollId = watch("BottomRollId");
	const bottomRollDiameter = watch("BottomRollDiameter") ?? null;
	const bottomRollType = watch("BottomRollType") ?? null;

	const bothRollsDefined = topRollId != null && bottomRollId != null;

	const availableLetters = ROLLSET_LETTERS.filter(
		(letter) => !activeLetters.has(letter),
	);

	const [selectedLetter, setSelectedLetter] = useState<string>(
		availableLetters[0] || "",
	);

	useEffect(() => {
		if (show) {
			setSelectedLetter(availableLetters[0] || "");
		}
	}, [show]);

	const createRollset = useMutation({
		mutationFn: upsertRollset,
		meta: { successMessage: "Rollset created successfully" },
		onSuccess: async (rollsetId) => {
			await queryClient.invalidateQueries({
				queryKey: ["readyRollsets", standId, sectionId],
			});
			onHide(rollsetId);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			reset({
				BottomRollId: null,
				TopRollId: null,
			});
			setInitialTopRoll(null);
			setInitialBottomRoll(null);
		},
	});

	const upsertRollMutation = useMutation({
		mutationFn: upsertRoll,
		meta: { successMessage: "Roll updated successfully" },
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const saveData = async () => {
		if (topRollId && initialTopRoll) {
			const topDiameterChanged = topRollDiameter !== initialTopRoll.diameter;
			const topStandChanged = initialTopRoll.standId != standId;

			if (topDiameterChanged || topStandChanged) {
				await upsertRollMutation.mutateAsync({
					partId: topRollId,
					diameter: topRollDiameter,
					standId: standId,
				});
			}
		}

		if (bottomRollId && initialBottomRoll) {
			const bottomDiameterChanged =
				bottomRollDiameter !== initialBottomRoll.diameter;
			const bottomStandChanged = initialBottomRoll.standId !== standId;

			if (bottomDiameterChanged || bottomStandChanged) {
				await upsertRollMutation.mutateAsync({
					partId: bottomRollId,
					diameter: bottomRollDiameter,
					standId: standId,
				});
			}
		}

		await createRollset.mutateAsync({
			standId: standId!,
			sectionId: sectionId!,
			rollsetLetter: selectedLetter,
			topRollId: topRollId,
			bottomRollId: bottomRollId,
		});
	};

	const handleSave = async () => {
		if (!sectionId || !standId || !selectedLetter) {
			setError("Section, stand and letter are required");
			return;
		}

		const isValid = await trigger();
		if (!isValid) return;

		if (topRollType == "E" && bottomRollType == "E") {
			const topRoll = rolls?.find((r) => r.PartId === topRollId);
			const bottomRoll = rolls?.find((r) => r.PartId === bottomRollId);
			const passError = getPassCompatibilityError(
				topRoll,
				bottomRoll,
				sectionId,
			);

			if (passError) {
				setError(passError);
				return;
			}
		}

		const diameterError = getDiameterError({
			topDiameter: topRollDiameter,
			bottomDiameter: bottomRollDiameter,
		});

		if (diameterError) {
			setConfirmMessage(
				`The diameters are different (Top: ${topRollDiameter}, Bottom: ${bottomRollDiameter}). Do you want to continue?`,
			);
			return;
		}

		saveData();
	};

	const handleCancel = () => {
		onHide();
		reset({
			TopRollId: null,
			BottomRollId: null,
		});
		setInitialTopRoll(null);
		setInitialBottomRoll(null);
	};

	return (
		<>
			<MesfModal
				open={show}
				handleClose={handleCancel}
				title="CREATE ROLLSET"
				maxWidth="xl"
				id="new-rollset-modal"
			>
				<MesfModal.Content
					dividers
					sx={{
						padding: (theme) => theme.spacing(3),
						minWidth: 600,
						overflowY: "scroll",
					}}
				>
					<Box
						sx={{
							marginBottom: (theme) => theme.spacing(1),
						}}
					>
						<Grid2 container spacing={1}>
							<Grid2 size={{ md: 4 }} mt={0.5}>
								<SectionSelector label="Section" value={sectionId} disabled />
							</Grid2>
							<Grid2 size={{ md: 4 }}>
								<TextField
									fullWidth
									label="Stand"
									variant="outlined"
									size="small"
									value={standName}
									disabled
								/>
							</Grid2>
							<Grid2 size={{ md: 4 }} mt={1}>
								<FormControl variant="outlined" size="small" fullWidth required>
									<InputLabel>Letter</InputLabel>
									<Select
										data-test-id="rollset-letter-selector"
										value={selectedLetter}
										onChange={(event) =>
											setSelectedLetter(event.target.value as string)
										}
										label="Letter"
										disabled={availableLetters.length === 0}
									>
										{availableLetters.map((letter) => (
											<MenuItem
												key={letter}
												value={letter}
												data-test-id={`rollset-letter-option-${letter}`}
											>
												{letter}
											</MenuItem>
										))}
									</Select>
									<Typography
										sx={{
											fontSize: "0.7rem",
											color: (theme) => theme.palette.text.secondary,
											marginTop: (theme) => theme.spacing(0.5),
										}}
									>
										Only letters without active rollsets
									</Typography>
								</FormControl>
							</Grid2>
						</Grid2>
					</Box>
					<Grid2
						size={{ xs: 12 }}
						sx={{
							marginBottom: (theme) => theme.spacing(1),
						}}
					>
						<Divider />
					</Grid2>
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							gap: 2,
						}}
					>
						<Box sx={{ flex: 1 }} data-test-id="top">
							<Controller
								name="TopRollId"
								control={control}
								rules={{
									validate: {
										sameRolls: (value, formValues) => {
											return value !== null && value === formValues.BottomRollId
												? "Can't be the same as Bottom Roll"
												: true;
										},
										sameType: (value, formValues) =>
											value !== null &&
											formValues.BottomRollId !== null &&
											formValues.TopRollType !== formValues.BottomRollType
												? "Must be the same type as Bottom Roll"
												: true,
									},
									deps: ["TopRollId", "BottomRollId"],
								}}
								render={({ field, fieldState }) => (
									<RollSelector
										label="Top Roll"
										value={field.value}
										fieldError={fieldState.error}
										sectionId={sectionId}
										standId={standId}
										diameter={topRollDiameter}
										diameterError={getDiameterError({
											topDiameter: topRollDiameter,
											bottomDiameter: bottomRollDiameter,
										})}
										onChange={(id, diameter, rollStandId, type) => {
											field.onChange(id);
											setValue("TopRollDiameter", diameter ?? 0);
											setValue("TopRollStandId", rollStandId ?? null);
											setValue("TopRollType", type!);
											setInitialTopRoll({
												diameter: diameter ?? null,
												standId: rollStandId ?? null,
											});
											trigger("BottomRollId");
										}}
										onDiameterChange={(diameter) => {
											setValue("TopRollDiameter", diameter ?? 0, {
												shouldValidate: true,
												shouldDirty: true,
											});
										}}
										params={{
											sectionId,
										}}
									/>
								)}
							/>
						</Box>
						<Box sx={{ flex: 1 }} data-test-id="bottom">
							<Controller
								name="BottomRollId"
								control={control}
								rules={{
									validate: {
										sameRolls: (value, formValues) => {
											return value !== null && value === formValues.TopRollId
												? "Can't be the same as Top Roll"
												: true;
										},
										sameType: (value, formValues) =>
											value !== null &&
											formValues.TopRollId !== null &&
											formValues.TopRollType !== formValues.BottomRollType
												? "Must be the same type as Top Roll"
												: true,
									},
									deps: ["TopRollId", "BottomRollId"],
								}}
								render={({ field, fieldState }) => (
									<RollSelector
										label="Bottom Roll"
										value={field.value}
										fieldError={fieldState.error}
										sectionId={sectionId}
										standId={standId}
										diameter={bottomRollDiameter}
										diameterError={getDiameterError({
											topDiameter: topRollDiameter,
											bottomDiameter: bottomRollDiameter,
										})}
										onChange={(id, diameter, rollStandId, type) => {
											field.onChange(id);
											setValue("BottomRollDiameter", diameter ?? 0);
											setValue("BottomRollStandId", rollStandId ?? null);
											setValue("BottomRollType", type!);
											setInitialBottomRoll({
												diameter: diameter ?? null,
												standId: rollStandId ?? null,
											});
											trigger("TopRollId");
										}}
										onDiameterChange={(diameter) => {
											setValue("BottomRollDiameter", diameter ?? 0, {
												shouldValidate: true,
												shouldDirty: true,
											});
										}}
										params={{
											sectionId,
										}}
									/>
								)}
							/>
						</Box>
					</Box>
				</MesfModal.Content>

				<MesfModal.Actions style={{ padding: "15px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3 }}>
							<Button
								variant="contained"
								color="inherit"
								onClick={handleCancel}
								disabled={
									createRollset.isPending || upsertRollMutation.isPending
								}
								fullWidth
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSave}
								startIcon={
									createRollset.isPending || upsertRollMutation.isPending ? (
										<CircularProgress size="1rem" color="inherit" />
									) : null
								}
								disabled={
									createRollset.isPending ||
									upsertRollMutation.isPending ||
									!sectionId ||
									!standId ||
									!selectedLetter ||
									availableLetters.length === 0 ||
									!bothRollsDefined
								}
								fullWidth
								data-test-id="create-rollset-button"
							>
								Create
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ConfirmationDialogRaw
				open={!!confirmMessage}
				onClose={(value?: string) => {
					if (value === "OK") {
						saveData();
					}
					setConfirmMessage("");
				}}
				title="Confirm Rollset Assembly"
				message="Roll diameters don't match. Are you sure you want to create this rollset?"
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Creating Rollset"
			/>
		</>
	);
};
